import { useTranslates } from 'providers/translates';
import { useAppSelector } from 'hooks';
import styles from '../../../About/styles.module.scss';
import { Link } from 'react-router-dom';
import { TProfile } from 'api/http-client/profileTypes';
import { useAuth } from 'providers/auth-provider';
import { isVac } from 'utilities/is-vac';
import { Language } from 'providers/translates/enums';

export const ContentMemorial = () => {
  const { translates: t, selectedLang } = useTranslates();
  const isFrench = selectedLang === Language.FRENCH;
  const user = useAuth().user as unknown as TProfile;
  const editableQuilt = useAppSelector((state) => state.quilt.editableQuilt);
  
  const nameRow = `Name associated with your account: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRow = `Email address associated with your account: ${user?.email ?? ''}\n`;
  const quiltNameRow = `Name of the quilt square you need help with: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRow = `Issue or concern:`;
  
  const nameRowVac = `Name associated with your account: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRowVac = `Email address associated with your account: ${user?.email ?? ''}\n`;
  const quiltNameRowVac = `Name of the quilt square you need help with: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRowVac = `Issue or concern:`;
  
  const nameRowFr = `Nom associé à votre compte: ${user?.firstName ?? ''} ${user?.lastName ?? ''}\n`;
  const emailRowFr = `Adresse courriel associée à votre compt: ${user?.email ?? ''}\n`;
  const quiltNameRowFr = `Nom du carreau de courtepointe pour lequel vous avez besoin d’aide: ${editableQuilt?.firstName ?? ''} ${editableQuilt?.lastName ?? ''}\n`;
  const issueOrConcernRowFr = `De quoi s’agit-il?`;
  
  let contactUsEmail: { subject: string; body: string; };
  
  contactUsEmail = {
    subject: encodeURIComponent('I have a question about the Canadian Healing Quilt'),
    body: encodeURIComponent(
      `\n\n\nNote: If you are a current Canadian Healing Quilt user and are looking for assistance please provide us with the following information so we can help you as easily as possible:\n\n${nameRow}${emailRow}${quiltNameRow}${issueOrConcernRow}`
    )
  }
  
  const anchorContactUsEmail = () => {
    return (
      <a href={`mailto:info@virtualhospice.ca?subject=${contactUsEmail.subject}&body=${contactUsEmail.body}`}>
        info@virtualhospice.ca
      </a>
    )
  }
  return (
    <>
      <h2 className={styles.titleH2}>{t?.howToUse?.searchSection?.title ?? 'Search for a memorial quilt square'}</h2>
      <p className={styles.bodyText}>
        {t?.howToUse?.searchSection?.description ?? (
          <>
            Locate a memorial by using the search feature on the home page and entering the person’s name. 
          </>
        )}
      </p>

      <h2 className={styles.titleH2}>{t?.howToUse?.createMemorialSection?.title ?? 'Creating a memorial quilt square'}</h2>
      <p className={styles.bodyText}>
        {t?.howToUse?.createMemorialSection?.description ?? (
          <>
            You can use a computer, laptop, tablet, or smartphone to create your own memorial quilt square. 
            Creating your quilt square is personal, and you can take as much time as you need. 
          </>
        )}
      </p>                                  
      <ul className='colorNavy'>
        {t?.howToUse?.createMemorialSection ? (
          t?.howToUse?.createMemorialInstruction?.map(item => (
            <li className={styles.list_item} key={item.id}>
              <span className={styles.list_dot}></span>{item.text}
            </li>
          ))
        ) : (
          <>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Select “Create a memorial” to get started.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>You will be asked for your full name, email address, and postal code to create an account. 
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>You will be guided through step-by-step instructions to
            </li>
          </>
        )}
      </ul>
      <ul className={styles.nestedList}>
        {t?.howToUse?.createMemorialNestedInstruction ? (
          t?.howToUse?.createMemorialNestedInstruction.map(item => (
            <li className={styles.list_item} id={item.id}>
              <span className={styles.list_dot}></span>{item.text}
            </li>
          ))
        ) : (
          <>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Create the memorial by adding photos and text, and by positioning your content.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Invite others to collaborate on the memorial.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Allow viewers to add tributes and condolences.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Share the memorial on the Canadian Healing Quilt or keep it private.
            </li>
            <li className={styles.list_item}>
              <span className={styles.list_dot}></span>Share the memorial with friends, families, colleagues, or others.
            </li>
          </>
        )}
      </ul>

      <h2 className={styles.titleH2}>{t?.howToUse?.tributesAndCondolencesSection?.title ?? 'Tributes and condolences'}</h2>
      <p className={styles.bodyText}>
        {t?.howToUse?.tributesAndCondolencesSection?.firstParagraph ?? (
          <>
            If you are the creator of a memorial, you can choose to allow viewers to add tributes or condolences or 
            to light a candle. You can also choose to delete any tributes or condolences. 
          </>
        )}
      </p>
      <p className={styles.bodyText}>
        {t?.howToUse?.tributesAndCondolencesSection?.secondParagraph ?? (
          <>
            If you are viewing a memorial, you can add a message or icon, or light a candle to show support. There 
            is no cost for this.
          </>
        )}
      </p>

      <h2 className={styles.titleH2}>{t?.howToUse?.inspirationSection?.title ?? 'Need some inspiration?'}</h2>
      <p className={styles.bodyText}>
        {t?.howToUse?.inspirationSection?.firstPart ?? 'Click'}{' '}
        <Link 
          className={styles.customLink}
          to={'/Healing-Quilt-Sample-Memorial.pdf'} 
          target="__blank" 
          rel="noreferrer"
        >
          {t?.howToUse?.inspirationSection?.link ?? 'here'}
        </Link>
        {' '}{t?.howToUse?.inspirationSection?.secondPart ?? 'to see some examples of completed quilt squares.'}
      </p>
      <h2 className={styles.titleH2}>{t?.howToUse?.griefSupportSection?.title ?? 'Grief support'}</h2>
      <p className={styles.bodyText}>
        {t?.howToUse?.griefSupportSection?.firstPart ?? (
          <>
            For assistance with grief, for yourself or those you care about, visit the support resources on our “
          </>
        )}
        <Link 
          className={styles.customLink}
          to={'/resources/?visible=grief-support'} 
          target="__blank" 
          rel="noreferrer"
        >
          {t?.howToUse?.griefSupportSection?.link ?? 'Resources'}
        </Link>
        {t?.howToUse?.griefSupportSection?.secondPart ?? (
          <>” page.</>
        )}
      </p>

      <h2 className={styles.titleH2}>{t?.howToUse?.userSupportSection?.title ?? 'User support'}</h2>
      <p className={styles.bodyText}>
        {t?.howToUse?.userSupportSection?.firstPart ?? (
          <>
            We have tried to ensure you have a positive experience, but technical difficulties can occur. If you have trouble using the 
            Canadian Healing Quilt, please refer to the “Frequently asked questions” below. If you need further support, email
          </>
        )}{' '}{anchorContactUsEmail()}.
      </p>

      <h2 className={styles.titleH2}>{t?.howToUse?.questionsSection?.title ?? 'Frequently asked questions'}</h2>
      <h3 className={styles.titleH3}>
        {t?.howToUse?.questionsSection?.firstSubtitle ?? (
          <>
            I didn’t get a confirmation email when I signed up. What should I do?
          </>
        )}
      </h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.firstParagraph ?? (
          <>
            The Canadian Healing Quilt automatically sends a confirmation email to the email address you used to make 
            your account. Check your “Junk” or “Spam” folder for the confirmation email to verify your account. If 
            you still need help, email us at
          </>
        )}{' '}{anchorContactUsEmail()}.
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.secondSubtitle ?? 'Is there a cost for creating a memorial?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.secondParagraph ?? (
          <>
            No. The Canadian Healing Quilt is completely free for Canadians. There are no hidden fees and no options for costly services. No one will ever try to sell you anything.
          </>
        )}
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.thirdSubtitle ?? 'Can I use a smartphone or tablet?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.thirdParagraph ?? (
          <>
            Yes. The Canadian Healing Quilt is compatible with all platforms using any browser (e.g., Chrome, Safari). You can use it on smartphones, tablets, or personal computers.
          </>
        )}{' '}{anchorContactUsEmail()}
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.fourthSubtitle ?? 'What if I still have questions?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.fourthParagraph ?? 'If you have questions, email us at'}{' '}
       {anchorContactUsEmail()}
        .
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.fifthSubtitle ?? 'What if I have technical issues?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.fifthParagraph ?? (
          <>
            If you have trouble accessing the Canadian Healing Quilt, logging in, or using any of the features, please email us at
          </>
        )}{' '}
       {anchorContactUsEmail()}
        .
      </p>
      <h3 className={styles.titleH3}>{t?.howToUse?.questionsSection?.sixthSubtitle ?? 'How can I help support this project?'}</h3>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.sixthParagraph ?? 'To help us maintain the HealingQuilt.ca,'}{' '}
        {!isVac() ? (
          <a 
          href="https://www.canadahelps.org/en/charities/the-international-centre-for-dignity-and-palliative-care-inc/" 
          className={styles.customLink} 
          target="__blank"
        >{t?.howToUse?.questionsSection?.donateLink ?? 'donate here'}</a>
        ) : (
        <a 
          href={isFrench ? "https://www.canadahelps.org/fr/organismesdebienfaisance/the-international-centre-for-dignity-and-palliative-care-inc/" :  "https://www.canadahelps.org/en/charities/the-international-centre-for-dignity-and-palliative-care-inc/" }
          className={styles.customLink} 
          target="__blank"
        >{t?.howToUse?.questionsSection?.donateLink ?? 'donate here'}</a>)}.
      </p>
      <p className={styles.bodyText}>
        {t?.howToUse?.questionsSection?.seventhParagraph ?? (
          <>
            We welcome new partnerships and opportunities to expand the awareness 
            and use of the Canadian Healing Quilt across Canada. To learn more or get involved, email
          </>
        )}{' '}
       {anchorContactUsEmail()}
        .
      </p>
    </>
  );
};
